import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone } from 'lucide-react';
import FacebookIcon from '../../../Assets/SocialmediaIcons/facebook.png';
import InstagramIcon from '../../../Assets/SocialmediaIcons/instagram.png';
import TwitterIcon from '../../../Assets/SocialmediaIcons/twitter.png';
import LinkedinIcon from '../../../Assets/SocialmediaIcons/linkedin.png';

import Logo from '../../../Assets/PTKCS Icon Logo.png';

const Footer = () => {
  const aboutLinks = [
    { label: 'Who We Are', to: '/insights/aboutus' },
    { label: 'Careers', to: '/' },
    { label: 'Webinars', to: '/insights/webinar' },
  ];

  const importantLinks = [
    { label: 'AI Development', to: '/services/ai-development' },
    { label: 'Generative AI', to: '/services/generative-ai-development' },
    { label: 'Cyber Security', to: '/services/cybersecurity' },
    { label: 'Cloud Migration', to: '/services/cloud-migration-managed-services' },
    { label: 'Enterprise Software Consulting', to: '/services/enterprise-software-consulting' },
    { label: 'Infor', to: '/services/infor' },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', to: '/privacy-policy' },
    { label: 'Terms of Use', to: '/terms-of-use' },
    { label: 'Cookie Policy', to: '/cookie-policy' },
  ];

  const socialLinks = [
    { icon: FacebookIcon, href: 'https://www.facebook.com/profile.php?id=100087640681232', alt: 'Facebook' },
    { icon: InstagramIcon, href: 'https://www.instagram.com/kcs_technologies_indonesia/?igsh=MWZvbGF1dHQxbXl5dw%3D%3D#', alt: 'Instagram' },
    { icon: TwitterIcon, href: 'https://twitter.com/pt_kcs', alt: 'X (Twitter)' },
    { icon: LinkedinIcon, href: 'https://www.linkedin.com/company/89743106/admin/', alt: 'LinkedIn' },
  ];

  const scrollToTop = () => window.scrollTo(0, 0);

  return (
    <footer className="bg-black font-jost text-white">
      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-6 lg:px-8 lg:py-10">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_1.85fr] lg:gap-12">
          <div className="max-w-xl">
            <Link to="/" onClick={scrollToTop} className="inline-grid h-28 w-28 shrink-0 place-items-center bg-white" aria-label="KCS Tech home">
              <img src={Logo} alt="KCS Tech" className="h-20 w-20 object-contain" />
            </Link>

            <p className="mt-4 max-w-xs text-base leading-6 text-white">
              Enterprise technology, AI, ERP, and consulting services built for teams that need dependable digital growth.
            </p>

            <div className="mt-6 flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.alt}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.alt}
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-white transition duration-200 hover:-translate-y-0.5"
                >
                  <img src={social.icon} alt="" className="h-6 w-6 object-contain" />
                </a>
              ))}
            </div>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <h4 className="text-sm font-bold uppercase tracking-[0.22em] text-white">Services</h4>
              <ul className="mt-4 space-y-2">
                {importantLinks.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      onClick={scrollToTop}
                      className="text-base text-white underline-offset-4 transition duration-200 hover:underline"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase tracking-[0.22em] text-white">About Us</h4>
              <ul className="mt-4 space-y-2">
                {aboutLinks.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      onClick={scrollToTop}
                      className="text-base text-white underline-offset-4 transition duration-200 hover:underline"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="sm:col-span-2 lg:col-span-1">
              <h4 className="text-sm font-bold uppercase tracking-[0.22em] text-white">Connect</h4>

              <div className="mt-4 space-y-3">
                <a href="tel:+6285218084208" className="flex items-center gap-3 text-base text-white underline-offset-4 hover:underline">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <span>+62 852 1808 4208</span>
                </a>
                <a href="mailto:info@kcs-tech.com" className="flex items-center gap-3 text-base text-white underline-offset-4 hover:underline">
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <span>info@kcs-tech.com</span>
                </a>
                <div className="flex items-start gap-3 text-base leading-6 text-white">
                  <MapPin className="mt-1 h-4 w-4 flex-shrink-0" />
                  <span>BSD Green Office Park 1, 6th Floor, Tangerang 15345, Indonesia</span>
                </div>
              </div>

              <Link
                to="/contact"
                onClick={scrollToTop}
                className="mt-6 inline-flex w-full items-center justify-center border border-white bg-white px-5 py-2.5 text-sm font-bold uppercase tracking-[0.16em] text-black transition duration-200 hover:bg-black hover:text-white"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/30 bg-black">
        <div className="mx-auto max-w-7xl px-5 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-white">
              &copy; {new Date().getFullYear()} KCS Tech. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {legalLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={scrollToTop}
                  className="text-sm text-white underline-offset-4 transition duration-200 hover:underline"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
